import { CarouselButtonPadding, TextSlideProps } from 'omakase/Carousel/Carousel';
import { Property } from 'csstype';
import { Theme, css } from '@emotion/react';

export const arrowBtn = (
  { breakpoints, spacing }: Theme,
  {
    direction,
    showOnMobile,
    padding,
    show,
  }: {
    direction: 'left' | 'right';
    showOnMobile: boolean;
    padding: CarouselButtonPadding;
    show: boolean;
  }
) => [
  css`
    opacity: ${show ? 1 : 0};
    position: absolute;
    top: 50%;
    transform: translate3d(0, -50%, 0);
    ${direction + ': ' + spacing(padding) + 'px'};
  `,
  direction === 'right' &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `,
  showOnMobile ||
    css`
      ${breakpoints.down('xs')} {
        display: none;
      }
    `,
];

export const dotBtnsContainer = (numDotsToDisplay: number) => css`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  max-width: ${numDotsToDisplay * 38}px;
  overflow: clip;
`;

export const dotBtnsContainerInner = css`
  display: flex;
  align-items: flex-end;
  transition: transform 0.2s cubic-bezier(0.455, 0.03, 0.515, 0.955);
`;

export const dotBtn = (selected: boolean) => [
  css`
    background-color: transparent;
    cursor: pointer;
    position: relative;
    padding: 0;
    outline: 0;
    border: 0;
    width: 30px;
    height: 30px;
    margin-right: 4px;
    margin-left: 4px;
    display: flex;
    align-items: center;

    &:after {
      background-color: rgba(255, 255, 255, 0.3);
      width: 100%;
      height: 2px;
      content: '';
    }
  `,
  selected &&
    css`
      &:after {
        background-color: rgba(255, 255, 255, 1);
      }
    `,
];

export const carouselBase = (showPointerCursor: boolean) => [
  css`
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  `,
  showPointerCursor &&
    css`
      &:hover {
        cursor: pointer;
      }
    `,
];

export const carouselViewport = css`
  overflow: hidden;
  height: 100%;
  width: 100%;
  position: relative;
  &:after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }
`;

export const carouselButtonGradient = css`
  &:after {
    background: linear-gradient(180deg, transparent 80%, rgba(0, 0, 0, 0.3));
  }
`;

export const carouselContainer = css`
  display: flex;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  height: 100%;
`;

export const carouselSlide = css`
  position: relative;
  flex: 0 0 100%;
`;

export const carouselSlideInner = css`
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const DEFAULT_EDGE_SPACING = 6;

export const carouselSlideText = (
  { colors, spacing, typography }: Theme,
  {
    variant,
    fgColor,
    bgColor,
    edgeSpacing = DEFAULT_EDGE_SPACING,
  }: Omit<TextSlideProps, 'content' | 'type'>
) => [
  typography.variant(variant),
  css`
    color: ${colors[fgColor]};
    background: ${colors[bgColor]};
    padding: ${spacing(edgeSpacing)}px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
  `,
];

export const carouselSlideTextInner = css`
  max-height: 100%;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6; // seems about right
  overflow: hidden;
`;

export const carouselSlideImg =
  (objectFit: Property.ObjectFit) =>
  ({ spacing }: Theme) => css`
    height: 100%;
    width: 100%;
    object-fit: ${objectFit};
    object-position: center;
    display: block;
  `;
